<script>
import { mapGetters, mapActions } from 'vuex';
import RefundModal from '@/components/courses/RefundModal';
import AddUserModal from '@/components/courses/AddUserModal';
import TableView from '@/components/elements/views/TableView';
import SendMessageModal from '@/components/courses/SendMessageModal';
import ChangeCourseModal from '@/components/courses/ChangeCourseModal';
import ConfirmCancelModal from '@/components/elements/ConfirmationModal';

export default {
    components: {
        AddUserModal,
        SendMessageModal,
        ChangeCourseModal,
        ConfirmCancelModal,
        RefundModal
    },

    page: {
        title: 'Marathon Users'
    },

    extends: TableView,

    data() {
        const {
            params: { id: courseId },
            query: { status = 'joined' }
        } = this.$route;

        return {
            course: {},
            courseId,
            userId: null,
            starsUsed: 0,
            paymentDetails: {},
            paymentMethod: null,
            activeTab: {
                value: status
            }
        };
    },

    computed: {
        ...mapGetters({
            STATUS_JOINED: 'courses/STATUS_JOINED',
            STATUS_CANCELLED: 'courses/STATUS_CANCELLED',
            STATUS_PENDING_PAYMENT: 'courses/STATUS_PENDING_PAYMENT',
            REFUNDABLE_PAYMENT_METHODS: 'courses/REFUNDABLE_PAYMENT_METHODS'
        }),

        labels() {
            return [
                { text: 'First name', value: 'user.firstName', width: '200px' },
                { text: 'Last name', value: 'user.lastName', width: '200px' },
                { text: 'Email', value: 'email', width: '200px' },
                { text: 'Phone', value: 'phone', width: '200px' },
                {
                    value: 'user.campuses',
                    text: 'Campuses',
                    width: '150px',
                    filterFieldName: 'user.campuses.name',
                    sortable: false,
                    filter: 'joinTable'
                },
                {
                    text: 'Hugs',
                    value: 'user.hugs',
                    width: '200px',
                    sortable: false,
                    filterable: false,
                    filter: v => v.map(hug => hug.number).join(', ')
                },
                {
                    text: 'Payment method',
                    value: 'paymentMethod',
                    width: '200px'
                },
                {
                    text: 'Price',
                    value: 'price',
                    width: '200px',
                    filter: 'formatPrice'
                },
                {
                    text: 'Stars used',
                    value: 'starsUsed',
                    type: 'number',
                    width: '200px'
                },
                {
                    value: 'isRefunded',
                    type: 'boolean',
                    isBadge: true,
                    badgeOptions: {
                        trueVariant: 'danger',
                        falseVariant: 'success'
                    },
                    width: '150px'
                },
                {
                    text: 'Refunded amount',
                    value: 'refundedPrice',
                    width: '200px',
                    type: 'number',
                    filter: 'formatPrice'
                },
                {
                    text: 'Registration date',
                    value: 'createdAt',
                    width: '150px',
                    type: 'date',
                    filter: 'formatDate'
                }
            ];
        },

        tabActions() {
            return [
                {
                    name: 'message',
                    text: 'Send message'
                }
            ];
        },

        tableActions() {
            return [
                {
                    icon: 'fas fa-user-times',
                    method: 'cancelRegistration',
                    where: {
                        status: {
                            value: [
                                this.STATUS_JOINED,
                                this.STATUS_PENDING_PAYMENT
                            ],
                            operator: 'in'
                        }
                    }
                },
                {
                    icon: 'fas fa-exchange-alt',
                    method: 'changeCourse',
                    where: {
                        status: {
                            value: this.STATUS_JOINED
                        }
                    }
                },
                {
                    icon: 'fas fa-money-bill-wave-alt',
                    method: 'refund',
                    where: {
                        isRefunded: {
                            value: false
                        },
                        paymentMethod: {
                            value: this.REFUNDABLE_PAYMENT_METHODS,
                            operator: 'in'
                        },
                        paymentDetailsId: {
                            value: null,
                            operator: 'not'
                        }
                    }
                }
            ];
        },

        tabsElements() {
            return [
                {
                    text: 'Joined',
                    value: this.STATUS_JOINED
                },
                {
                    text: 'Pending payment',
                    value: this.STATUS_PENDING_PAYMENT
                },
                {
                    text: 'Cancelled',
                    value: this.STATUS_CANCELLED
                }
            ];
        },

        title() {
            return `${this.course.name}'s users`;
        },

        breadcrumbs() {
            return [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Marathons',
                    to: '/courses'
                },
                {
                    text: this.course.name,
                    to: `/courses/${this.course.id}/edit`
                },
                {
                    text: 'Users',
                    active: true
                }
            ];
        },

        additionalFetchData() {
            return {
                id: this.courseId,
                status: this.activeTab.value
            };
        },

        options() {
            const options = {
                addButtonText: 'Add user',
                selectField: 'userId'
            };

            if (this.activeTab.value) {
                options.linkSuffix = `&status=${this.activeTab.value}`;
            }

            return options;
        },

        additionalComponents() {
            return [
                {
                    name: 'send-message-modal',
                    props: {
                        courseId: this.courseId,
                        group: this.activeTab
                    }
                },
                {
                    name: 'add-user-modal',
                    props: {
                        courseId: this.courseId
                    },
                    events: {
                        added: this.fetchItems
                    }
                },
                {
                    name: 'confirm-cancel-modal',
                    props: {
                        id: 'confirm-cancel-modal',
                        title: 'Cancel registration'
                    },
                    events: {
                        agree: this.onCancel
                    }
                },
                {
                    name: 'change-course-modal',
                    props: {
                        course: this.courseId,
                        userId: this.userId
                    },
                    events: {
                        success: this.removeItem
                    }
                },
                {
                    name: 'refund-modal',
                    props: {
                        courseId: this.courseId,
                        userId: this.userId,
                        paymentDetails: this.paymentDetails || {},
                        starsUsed: this.starsUsed,
                        paymentMethod: this.paymentMethod
                    },
                    events: {
                        success: this.onRefunded
                    }
                }
            ];
        }
    },

    watch: {
        activeTab() {
            this.pagination.currentPage = 1;
            this.pagination.sortBy = 'Course2User.createdAt';
            this.fetchItems();
        }
    },

    async created() {
        try {
            this.course = await this.getCourse(this.courseId);
        } catch (err) {
            console.error(err);

            this.$toasterError();
        }
    },

    methods: {
        ...mapActions({
            getCourse: 'courses/show',
            getItems: 'courses/getUsers',
            cancel: 'courses/cancelRegistration'
        }),

        cancelRegistration(id, index) {
            this.userId = this.items[index].userId;
            this.$bvModal.show('confirm-cancel-modal');
        },

        changeCourse(id, index) {
            this.userId = this.items[index].userId;
            this.$bvModal.show('course-change-course');
        },

        refund(id, index) {
            this.userId = this.items[index].userId;
            this.starsUsed = this.items[index].starsUsed;
            this.paymentMethod = this.items[index].paymentMethod;
            this.paymentDetails = this.items[index].paymentDetails;
            this.$bvModal.show('course-refund');
        },

        async onCancel() {
            try {
                await this.cancel({
                    id: this.courseId,
                    userId: this.userId
                });

                this.$toaster('User has been removed!');

                this.removeItem(this.userId);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        onRefunded({ userId, amount }) {
            const item = this.items.find(item => item.userId === userId);

            if (item) {
                item.paymentDetails.isRefunded = true;
                item.isRefunded = true;
                item.refundedPrice = amount;
            }

            this.paymentDetails = {};
        },

        removeItem(userId) {
            if (this.activeTab.value) {
                this.items = this.items.filter(item => item.userId !== userId);
                this.userId = null;
                this.pagination.total--;
            } else {
                const item = this.items.find(item => item.userId === userId);

                if (item) {
                    item.status = this.STATUS_CANCELLED;
                }
            }
        },

        onTabAction(name) {
            if (name === 'message') {
                this.$bvModal.show('course-send-message');
            }
        },

        onAddButtonClick() {
            this.$bvModal.show('course-add-user');
        }
    }
};
</script>
